import { useEffect, useState } from "react";
import "@/App.css";
import "@/editorial-overrides.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation, useNavigationType } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Menu, X, Instagram, ArrowRight, ArrowDown, Check, LockKeyhole, Download, ChevronDown } from "lucide-react";
import { COUNTRIES } from "@/countries";
import { JOURNEYS, findJourney } from "@/journeys";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NOUA_LOGO = "/noua-wordmark-transparent.png";
const SITE_TITLE = "NOUA — The Experience Studio";
function useTitle(t){useEffect(()=>{const prev=document.title;document.title=t?`${t} · NOUA`:SITE_TITLE;return()=>{document.title=prev}},[t])}

const images={hero:"https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=1800&q=85", pool:"https://images.unsplash.com/photo-1592396355679-1e2a094e8bf1?auto=format&fit=crop&w=1200&q=85", boat:"https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=1200&q=85", room:"https://images.unsplash.com/photo-1445019980597-93fa8acb246c?auto=format&fit=crop&w=1200&q=85", horizon:"https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=1400&q=85", safari:"https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1400&q=85", street:"https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&w=1400&q=85", road:"https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1200&q=85", cyclades:"https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?auto=format&fit=crop&w=1200&q=85", highlands:"https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1200&q=85"};
function Header(){const [open,setOpen]=useState(false);return <header className="site-header"><Link to="/" className="logo" data-testid="site-logo"><img className="logo-image" src={NOUA_LOGO} alt="NOUA"/></Link><nav className={open?"nav open":"nav"}>{[['Curated','/curated'],['Collective','/collective'],['Concierge','/concierge'],['Journeys','/journeys'],['Journal','/journal'],['About','/about']].map(([x,y])=><Link data-testid={`nav-${x.toLowerCase()}`} key={x} to={y} onClick={()=>setOpen(false)}>{x}</Link>)}<Link className="nav-cta" data-testid="nav-begin-journey" to="/begin">Begin your journey <ArrowUpRight size={15}/></Link></nav><button className="menu-button" data-testid="mobile-menu-button" onClick={()=>setOpen(!open)} aria-label="Toggle navigation">{open?<X/>:<Menu/>}</button></header>}
function Footer(){return <footer><div><div className="logo"><img className="logo-image" src={NOUA_LOGO} alt="NOUA"/></div><p className="muted">A considered way to go further.</p></div><div className="footer-links"><Link data-testid="footer-journal" to="/journal">Journal</Link><Link data-testid="footer-about" to="/about">About NOUA</Link><a data-testid="footer-instagram" href="https://www.instagram.com/nouathestudio/" target="_blank" rel="noreferrer"><Instagram size={16}/> Instagram</a></div><small>© 2026 NOUA Experience Studio</small></footer>}
function Layout({children}){return <><Header/>{children}<Footer/></>}
const pathways=[{name:'Curated',kicker:'For the quietly particular',text:'Bespoke journeys for individuals and couples, shaped around how you want to feel when you return.',img:images.room},{name:'Collective',kicker:'For the together-minded',text:'Thoughtfully orchestrated travel for groups, families, and friends who want the details to disappear.',img:images.pool},{name:'Concierge',kicker:'For the high-intent traveller',text:'An ongoing, highly personal travel relationship for lives that move often and well.',img:images.boat}];
const stories=[
  {slug:'the-art-of-taking-the-long-way',title:'The art of taking the long way',kicker:'FIELD NOTE / 01',dateline:'Notes from Umbria · Winter 2026',image:images.road,excerpt:'On slow driving, empty hours, and the discipline of arriving late on purpose.',body:[
    'Somewhere between Perugia and Assisi there is a road that no one recommends. It curves through hills that have watched five hundred years of pilgrims, and it adds an hour and a half to the drive.',
    'We took it on purpose. That is the beginning of a NOUA journey — the willingness to give the journey more time than it asks for.',
    'The long way is not romantic. It is deliberate. It is the antidote to the compressed itinerary, the four-cities-in-five-days habit that leaves travellers exhausted and their memories flat.',
    'Instead: one road, one lunch, one conversation in a village square. A window seat that faces the direction of light. A car that stops at anything that looks worth stopping for.',
    'When you return, people ask what you did. You will say very little. You will not have photographs of monuments. You will have the smell of woodsmoke and the taste of a wine that does not export. You will have paid attention.'
  ]},
  {slug:'a-table-in-the-cyclades',title:'A table in the Cyclades',kicker:'FIELD NOTE / 02',dateline:'Notes from the Aegean · Spring 2026',image:images.cyclades,excerpt:'On the small tavernas that never make the lists, and why we still find them.',body:[
    'The best table in the Cyclades is not on any website. It seats twelve. It faces a wall of bougainvillea that has been climbing the same stones since the innkeeper\'s grandmother was a girl.',
    'You get there by asking three people. Not concierge staff at a five-star. Not a guidebook. A fisherman, a bookshop owner, and a woman who sells honey by the roadside.',
    'This is the part of travel that cannot be booked, and it is the part that matters most. It is why NOUA does not send our travellers to the loudest room in the loudest hotel.',
    'What we can do is spend the time to know these places ourselves. To know who owns them, when they close, what nights they play music, and which corner catches the last of the light.',
    'The reservation is easy. The knowing is the work.'
  ]},
  {slug:'notes-from-the-highlands',title:'Notes from the highlands',kicker:'FIELD NOTE / 03',dateline:'Notes from Scotland · Autumn 2025',image:images.highlands,excerpt:'On weather as a companion, and why the wet days are often the best ones.',body:[
    'It rained for four of the five days we were in Perthshire. This is the sort of detail that sinks a trip in most trip reports. It is the reason it worked.',
    'The rain made the light strange. It made the whisky matter. It sent us inside, into rooms with fires, and into the kind of conversations that only happen when there is no next thing to rush to.',
    'A NOUA journey does not treat weather as failure. Neither should you. The weather is part of the place. The place is not for photographs; it is for the person you are while you are in it.',
    'On the fifth day the sky opened. We drove three hours to a loch we had not planned to see, and we ate cold sandwiches in a car park while a rainbow sat over the water.',
    'This is the sort of detail that makes it back into stories, years later, at other tables, in other countries. This is what we are trying to make more of.'
  ]}
];
function Home(){useTitle('');return <Layout><main><section className="hero" style={{backgroundImage:`url(${images.hero})`}}><div className="hero-copy"><p className="eyebrow">NOUA / THE EXPERIENCE STUDIO</p><h1>Go further,<br/><em>with intention.</em></h1><p className="hero-lede">The world, curated for <em>you.</em></p><Link className="button light" data-testid="hero-begin-journey" to="/begin">Begin your journey <ArrowRight size={16}/></Link></div><div className="scroll-note">Scroll to explore <span><ArrowDown size={16} strokeWidth={1.4}/></span></div></section><section className="intro band"><div className="section-index">01 / THE NOUA METHOD</div><div className="intro-text"><h2>Not more travel.<br/><em>More meaning.</em></h2><Link className="text-link" data-testid="about-noua-link" to="/about">Discover our point of view <ArrowRight size={16}/></Link></div></section><section className="pathways band"><div className="section-index">02 / FIND YOUR WAY</div><div className="pathway-grid">{pathways.map((p,i)=><Link to={`/${p.name.toLowerCase()}`} className="pathway" data-testid={`pathway-${p.name.toLowerCase()}`} key={p.name}><div className="pathway-image"><img src={p.img} alt={`${p.name} travel experience`} /><span>0{i+1}</span></div><p className="eyebrow">{p.kicker}</p><h3>{p.name}{p.name==='Concierge'&&<small className="coming-label"> — coming soon</small>}</h3><p>{p.text}</p><span className="circle-arrow"><ArrowUpRight size={18}/></span></Link>)}</div></section><section className="method band" data-testid="how-noua-works"><div className="section-index">03 / HOW NOUA WORKS</div><div className="method-heading"><h2>A different way<br/><em>to travel.</em></h2><p className="method-lede">We design around your rhythm, not a checklist.</p></div><div className="method-grid">{[
  {n:'01',k:'TELL US',t:"Share where you're going, who you're travelling with, and what matters to you. You can arrive with a destination in mind — or simply with the feeling that you want to go somewhere."},
  {n:'02',k:'WE LISTEN',t:'We take the time to understand how you travel: your pace, preferences, interests, comforts, dislikes and the things that make a journey memorable for you.'},
  {n:'03',k:'WE CURATE',t:'We research, edit and design around you. From destinations and neighbourhoods to hotels, restaurants and experiences, every recommendation has a reason for being there.'},
  {n:'04',k:'YOU TRAVEL',t:'Receive a journey designed around your rhythm. For Curated and Collective, you make your own travel arrangements. For Concierge, NOUA can take care of the details and reservations for you.'}
].map(s=><div className="method-step" data-testid={`method-step-${s.n}`} key={s.n}><span className="method-number">{s.n}</span><p className="eyebrow">— {s.k}</p><p className="method-text">{s.t}</p></div>)}</div></section><section className="statement band"><img src={images.horizon} alt="A warm horizon at the edge of a coastal village"/><div><p className="eyebrow">A DIFFERENT KIND OF ARRIVAL</p><h2>Leave room<br/><em>for wonder.</em></h2><Link className="button dark" data-testid="statement-begin-journey" to="/begin">Begin your journey <ArrowRight size={16}/></Link></div></section><Community/></main></Layout>}
function Community(){
  const [done,setDone]=useState(false);
  const [email,setEmail]=useState('');
  const [error,setError]=useState('');
  const [loading,setLoading]=useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const clean = (email || '').trim().toLowerCase();
    if (!clean) { setError('Please enter your email address.'); return; }
    // simple client-side email shape check (accepts most legitimate addresses)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) { setError('Please enter a valid email address.'); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/community`, {email: clean, first_name: 'Friend', consent: true}, {timeout: 20000});
      if (r && r.data && r.data.email_sent === false) {
        setError('You are on the list, but our welcome email did not send. Please write to studio@noua.in and we will follow up.');
      } else {
        setDone(true);
      }
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      setError(typeof detail === 'string' ? detail : 'We could not add you just now. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };
  return <section className="community band">
    <div><p className="eyebrow">THE NOUA CIRCLE</p><h2>For the in-between<br/><em>moments.</em></h2></div>
    {done
      ? <div className="success" data-testid="community-success"><Check/> You're on the list.</div>
      : <form onSubmit={submit} noValidate>
          <p>Occasional notes on places worth going, and the spaces between.</p>
          <div className="inline-form">
            <input
              data-testid="community-email-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="Your email address"
              value={email}
              onChange={e=>setEmail(e.target.value)}
              disabled={loading}
              aria-label="Your email address"
            />
            <button data-testid="community-submit-button" type="submit" aria-label="Join the NOUA Circle" disabled={loading}>
              <ArrowRight/>
            </button>
          </div>
          {error && <small className="form-error" data-testid="community-error">{error}</small>}
          <small>By joining, you agree to receive NOUA notes. Unsubscribe anytime.</small>
        </form>}
  </section>;
}
function StaticPage({type}){const p=pathways.find(x=>x.name.toLowerCase()===type);const copy={about:{kicker:'THE NOUA WAY',title:'We travel to pay attention.',text:'NOUA exists for the curious, the particular, and the ones who know that the best journeys are rarely the most obvious ones.'},journal:{kicker:'FIELD NOTES',title:'A slower way of seeing.',text:'Dispatches, itineraries, and observations from the road — made for the part of you that is already wondering where next.'}};const c=p||copy[type];useTitle(p?p.name:(type==='about'?'About':(type==='journal'?'Journal':type)));return <Layout><main className="subpage"><section className="sub-hero"><div><p className="eyebrow">{c.kicker||`NOUA / ${type.toUpperCase()}`}</p><h1>{c.title||c.name}</h1>{type==='concierge'&&<p className="subpage-coming-soon"><em>coming soon</em></p>}<p className="lede">{c.text}</p>{p&&<Link className="button dark" data-testid={`${type}-enquire-button`} to={`/begin?path=${type}`}>Tell us where you’re headed <ArrowUpRight size={16}/></Link>}</div><img src={p?.img||(type==='about'?images.street:images.safari)} alt={p?`${p.name} travel experience`:(type==='about'?"A quiet flower-lined street":"NOUA travel story")}/></section>{type==='journal'?<section className="journal-grid band">{stories.map((s,i)=><Link to={`/journal/${s.slug}`} className="journal-card" data-testid={`journal-article-${i}`} key={s.slug}><img src={s.image} alt={s.title}/><p className="eyebrow">{s.kicker}</p><h3>{s.title}</h3><p className="journal-excerpt">{s.excerpt}</p><span className="text-link">Read story <ArrowRight size={16}/></span></Link>)}</section>:<section className="prose band"><p>{type==='about'?'We design around your rhythm, not a checklist. Every recommendation begins with a question: what would make this feel truly yours?':'From the first conversation to the last small detail, we make space for the parts that cannot be booked — the right morning light, a familiar face, a little more time.'}</p></section>}</main></Layout>}
function CountrySelect({value, onChange, testid}){
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = q.trim()
    ? COUNTRIES.filter(c => c.name.toLowerCase().includes(q.toLowerCase()) || c.dial.includes(q.replace(/\s/g,'')))
    : COUNTRIES;
  useEffect(() => {
    if(!open) return;
    const close = (e) => { if(!e.target.closest('.country-select')) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return <div className={`country-select${open?' open':''}`} data-testid={testid}>
    <button type="button" className="country-trigger" data-testid={`${testid}-trigger`} onClick={()=>setOpen(o=>!o)} aria-haspopup="listbox" aria-expanded={open}>
      {value ? <span className="country-value"><span className="country-flag" aria-hidden="true">{value.flag}</span><span className="country-name">{value.name}</span><span className="country-dial">{value.dial}</span></span>
             : <span className="country-placeholder">Select your country</span>}
      <ChevronDown size={16} strokeWidth={1.4}/>
    </button>
    {open && <div className="country-dropdown" role="listbox">
      <input autoFocus className="country-search" data-testid={`${testid}-search`} type="text" placeholder="Search country or code" value={q} onChange={e=>setQ(e.target.value)}/>
      <ul>
        {filtered.length === 0 && <li className="country-empty">No matches</li>}
        {filtered.map(c => (
          <li key={c.code}>
            <button type="button" data-testid={`${testid}-option-${c.code}`} className={value?.code===c.code?'country-option selected':'country-option'} onMouseDown={(e)=>{ e.preventDefault(); onChange(c); setQ(''); setOpen(false); }}>
              <span className="country-flag" aria-hidden="true">{c.flag}</span>
              <span className="country-name">{c.name}</span>
              <span className="country-dial">{c.dial}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>}
  </div>;
}

function Begin(){
  useTitle('Begin your journey');
  const [step, setStep] = useState(1);
  const rawPath = new URLSearchParams(window.location.search).get('path') || '';
  const [path, setPath] = useState(rawPath ? rawPath[0].toUpperCase()+rawPath.slice(1).toLowerCase() : '');
  const [country, setCountry] = useState(null);
  const [phoneLocal, setPhoneLocal] = useState('');
  const [form, setForm] = useState({name:'', email:'', timeline:'', travellers:'', details:'', consent:false});
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const update = e => setForm({...form, [e.target.name]: e.target.type==='checkbox'?e.target.checked:e.target.value});

  const buildE164 = () => country ? `${country.dial}${phoneLocal.replace(/\D/g,'')}` : '';

  const submit = async e => {
    e.preventDefault();
    setError('');
    if (step === 2) {
      if (!country) { setError('Please select your country code.'); return; }
      const digits = phoneLocal.replace(/\D/g,'');
      if (!digits) { setError('Please enter your phone number to continue.'); return; }
      if (digits.length < 6 || digits.length > 15) { setError('Please enter a valid phone number.'); return; }
    }
    if (step < 3) { setStep(step + 1); return; }
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        pathway: path,
        country: country.name,
        country_code: country.dial,
        phone_local: phoneLocal.replace(/\D/g,''),
        phone_e164: buildE164(),
        consent: form.consent
      });
      setDone(true);
    } catch(err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Please check your details and try again.');
    }
  };

  if (done) return <Layout>
    <section className="form-page success-page" data-testid="enquiry-success">
      <Check/>
      <p className="eyebrow">YOUR ENQUIRY IS WITH US</p>
      <h1>Thank you for<br/><em>sharing.</em></h1>
      <p>Thank you for sharing the details with us. We’ve received your enquiry and will be in touch shortly. We look forward to beginning the conversation.</p>
      <Link className="button dark" data-testid="enquiry-success-home" to="/">Return home <ArrowRight size={16}/></Link>
    </section>
  </Layout>;

  return <Layout>
    <section className="form-page">
      <div className="form-heading">
        <p className="eyebrow">START YOUR NOUA</p>
        <h1>A few good<br/><em>questions.</em></h1>
        <p>Tell us enough to begin a useful conversation. No commitment, just a considered first step.</p>
      </div>
      <div className="progress" data-testid="form-progress">
        <span>0{step}</span><div><i style={{width:`${step*33.33}%`}}/></div><span>03</span>
      </div>
      <form onSubmit={submit} data-testid="enquiry-form">
        {step===1 && <div className="form-step">
          <label>Which way are you drawn?
            <select data-testid="pathway-select" required value={path} onChange={e=>setPath(e.target.value)}>
              <option value="">Select a pathway</option>
              <option>Curated</option><option>Collective</option><option>Concierge</option>
            </select>
          </label>
          <label>When are you thinking of travelling?
            <input data-testid="timeline-input" name="timeline" value={form.timeline} onChange={update} placeholder="A season, a month, or simply ‘soon’"/>
          </label>
        </div>}

        {step===2 && <div className="form-step">
          <label>Your name
            <input data-testid="name-input" name="name" required value={form.name} onChange={update} placeholder="First and last name"/>
          </label>
          <label>Email address
            <input data-testid="email-input" type="email" name="email" required value={form.email} onChange={update} placeholder="you@example.com"/>
          </label>
          <label>Phone number
            <CountrySelect value={country} onChange={setCountry} testid="country-select"/>
            <div className="phone-row">
              <span className="phone-dial" data-testid="phone-dial">{country ? country.dial : '+—'}</span>
              <input data-testid="phone-input" type="tel" inputMode="tel" value={phoneLocal} onChange={e=>setPhoneLocal(e.target.value)} placeholder="98765 43210"/>
            </div>
          </label>
          <label>Who is coming?
            <input data-testid="travellers-input" name="travellers" value={form.travellers} onChange={update} placeholder="Just me, two of us, a group…"/>
          </label>
        </div>}

        {step===3 && <div className="form-step">
          <label>What would make this journey yours?
            <textarea data-testid="details-input" name="details" value={form.details} onChange={update} placeholder="Share whatever feels useful — a feeling, a place, a non-negotiable…"/>
          </label>
          <label className="check-label">
            <input data-testid="consent-checkbox" type="checkbox" name="consent" required checked={form.consent} onChange={update}/>
            <span>I agree to NOUA contacting me about this enquiry and have read the <u>privacy note</u>.</span>
          </label>
        </div>}

        {error && <p className="form-error" role="alert" data-testid="enquiry-error">{error}</p>}
        <button className="button dark form-next" data-testid="enquiry-next-button" type="submit">
          {step===3?'Send enquiry':'Continue'} <ArrowRight size={16}/>
        </button>
      </form>
    </section>
  </Layout>;
}
function Login(){useTitle('Studio sign in');const [email,setEmail]=useState('');const [password,setPassword]=useState('');const nav=useNavigate();return <section className="admin-login"><div className="login-mark logo">NOUA<span>°</span></div><div className="login-box"><LockKeyhole/><p className="eyebrow">PRIVATE STUDIO</p><h1>Welcome back.</h1><form onSubmit={async e=>{e.preventDefault();try{await axios.post(`${API}/auth/login`,{email,password},{withCredentials:true});nav('/admin')}catch(err){alert(err.response?.data?.detail||'Unable to sign in')}}}><label>Email<input data-testid="admin-email-input" required type="email" value={email} onChange={e=>setEmail(e.target.value)}/></label><label>Password<input data-testid="admin-password-input" required type="password" value={password} onChange={e=>setPassword(e.target.value)}/></label><button className="button dark" data-testid="admin-login-button">Enter studio <ArrowRight size={16}/></button></form><Link data-testid="admin-back-link" to="/">Back to NOUA</Link></div></section>}
function Admin(){useTitle('Operations');const [items,setItems]=useState([]);const [selected,setSelected]=useState(null);const [exporting,setExporting]=useState(false);const nav=useNavigate();useEffect(()=>{axios.get(`${API}/admin/enquiries`,{withCredentials:true}).then(r=>setItems(r.data)).catch(()=>nav('/admin/login'))},[nav]);const update=async(status)=>{const r=await axios.patch(`${API}/admin/enquiries/${selected.id}/status`,{status},{withCredentials:true});setSelected(r.data);setItems(items.map(x=>x.id===r.data.id?r.data:x))};const exportCsv=async()=>{setExporting(true);try{const r=await axios.get(`${API}/admin/enquiries/export`,{withCredentials:true,responseType:'blob'});const url=URL.createObjectURL(new Blob([r.data],{type:'text/csv'}));const a=document.createElement('a');a.href=url;a.download=`noua-enquiries-${new Date().toISOString().slice(0,10)}.csv`;document.body.appendChild(a);a.click();a.remove();URL.revokeObjectURL(url);}catch(err){alert('Export failed. Please try again.')}finally{setExporting(false)}};return <div className="admin"><header className="admin-head"><Link className="logo" to="/"><img className="logo-image" src={NOUA_LOGO} alt="NOUA"/></Link><span>OPERATIONS / ENQUIRIES</span><button className="admin-export" data-testid="admin-export-button" onClick={exportCsv} disabled={exporting||items.length===0}><Download size={14}/> {exporting?'Preparing…':'Export CSV'}</button><button data-testid="admin-logout-button" onClick={async()=>{await axios.post(`${API}/auth/logout`,{},{withCredentials:true});nav('/admin/login')}}>Sign out</button></header><div className="admin-body"><aside><p className="eyebrow">INBOX</p><h1>Enquiries <span>{items.length}</span></h1>{items.length===0?<p className="muted" data-testid="empty-inbox">No enquiries yet.</p>:items.map(x=><button data-testid={`enquiry-row-${x.id}`} className={selected?.id===x.id?'inbox-row active':'inbox-row'} onClick={()=>setSelected(x)} key={x.id}><b>{x.name}</b><small>{x.pathway} · {new Date(x.created_at).toLocaleDateString()}</small><em>{x.status}</em></button>)}</aside><section className="admin-detail">{selected?<><p className="eyebrow">{selected.pathway} / ENQUIRY</p><h2>{selected.name}</h2><a data-testid="selected-email-link" href={`mailto:${selected.email}`}>{selected.email}</a><div className="detail-meta"><span>Timeline <b>{selected.timeline||'Not specified'}</b></span><span>Travellers <b>{selected.travellers||'Not specified'}</b></span></div><div className="detail-copy"><p>{selected.details||'No additional details shared.'}</p></div><div className="status-actions"><span>STATUS</span>{['new','in review','contacted','closed'].map(s=><button data-testid={`status-${s.replace(' ','-')}-button`} className={selected.status===s?'selected':''} onClick={()=>update(s)} key={s}>{s}</button>)}</div></>:<div className="empty-detail"><p className="eyebrow">SELECT AN ENQUIRY</p><h2>Your inbox,<br/><em>at a glance.</em></h2></div>}</section></div></div>}

function Unsubscribe(){
  useTitle('Unsubscribed from the NOUA Circle');
  const {token} = useParams();
  const [state, setState] = useState('working');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/community/unsubscribe/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setState(r.data && r.data.ok ? 'done' : 'invalid');
      } catch(_e) {
        if (cancelled) return;
        setState('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);
  return <Layout>
    <main className="subpage">
      <section className="unsubscribe band" data-testid="unsubscribe-page">
        <p className="eyebrow">THE NOUA CIRCLE</p>
        {state === 'working' && (<>
          <h1>One moment.</h1>
          <p className="lede" data-testid="unsubscribe-working">Updating your preferences…</p>
        </>)}
        {(state === 'done' || state === 'invalid') && (<>
          <h1 data-testid="unsubscribe-heading">You're<br/><em>all set.</em></h1>
          <p className="lede" data-testid="unsubscribe-copy">You have been unsubscribed from the NOUA Circle and will no longer receive our Circle emails.</p>
          <p className="unsubscribe-note">If you ever wish to return, we'd be delighted to have you.</p>
          <div className="unsubscribe-mark">
            <span className="unsubscribe-noua">NOUA</span>
            <span className="unsubscribe-tag">The Experience Studio</span>
          </div>
          <Link className="text-link" data-testid="unsubscribe-home-link" to="/">Return to NOUA <ArrowRight size={16}/></Link>
        </>)}
      </section>
    </main>
  </Layout>;
}


function Story(){
  const {slug}=useParams();
  const story=stories.find(s=>s.slug===slug);
  useTitle(story?story.title:'Journal');
  if(!story){
    return <Layout><main className="subpage"><section className="story-not-found band"><p className="eyebrow">FIELD NOTES</p><h1>This story has wandered off.</h1><Link className="text-link" data-testid="story-back-journal" to="/journal">Return to the Journal <ArrowRight size={16}/></Link></section></main></Layout>;
  }
  const others=stories.filter(s=>s.slug!==slug);
  return <Layout><main className="subpage story"><section className="story-hero"><img src={story.image} alt={story.title}/><div className="story-hero-copy"><p className="eyebrow" data-testid="story-kicker">{story.kicker}</p><h1 data-testid="story-title">{story.title}</h1><p className="story-dateline">{story.dateline}</p></div></section><section className="story-body band">{story.body.map((p,i)=><p key={`${story.slug}-p-${i}`} data-testid={`story-paragraph-${i}`}>{p}</p>)}<div className="story-signoff"><span className="eyebrow">— NOUA Field Notes</span><Link className="button dark" data-testid="story-begin-cta" to="/begin">Begin your journey <ArrowRight size={16}/></Link></div></section><section className="story-more band"><p className="eyebrow">MORE FROM THE JOURNAL</p><div className="story-more-grid">{others.map(o=><Link to={`/journal/${o.slug}`} className="journal-card" data-testid={`story-related-${o.slug}`} key={o.slug}><img src={o.image} alt={o.title}/><p className="eyebrow">{o.kicker}</p><h3>{o.title}</h3><span className="text-link">Read story <ArrowRight size={16}/></span></Link>)}</div></section></main></Layout>;
}

function JourneysList(){
  useTitle('Journeys');
  return <Layout><main className="subpage journeys">
    <section className="journeys-hero">
      <p className="eyebrow" data-testid="journeys-eyebrow">NOUA / JOURNEYS</p>
      <h1 data-testid="journeys-title">A destination is a beginning,<br/><em>not a checklist.</em></h1>
      <p className="lede">A quiet library of considered NOUA journeys. Each one is a starting point — a way of moving through a place — that can be reshaped around how you travel.</p>
    </section>
    <section className="journeys-grid band">
      {JOURNEYS.map((j,i)=>(
        <Link to={`/journeys/${j.slug}`} className="journey-card" data-testid={`journey-card-${j.slug}`} key={j.slug}>
          <div className="journey-card-image">
            <img src={j.cardImage} alt={j.cardTitle}/>
            <span>0{i+1}</span>
          </div>
          <p className="eyebrow">{j.destination}</p>
          <h3>{j.cardTitle}</h3>
          <p className="journey-card-sub">{j.cardSubtitle}</p>
          <span className="text-link">Explore this journey <ArrowRight size={16}/></span>
        </Link>
      ))}
    </section>
  </main></Layout>;
}

function JourneyDetail(){
  const {slug}=useParams();
  const j=findJourney(slug);
  useTitle(j?j.cardTitle:'Journey');
  if(!j){
    return <Layout><main className="subpage"><section className="story-not-found band"><p className="eyebrow">NOUA / JOURNEYS</p><h1>This journey has wandered off.</h1><Link className="text-link" data-testid="journey-back-list" to="/journeys">Return to Journeys <ArrowRight size={16}/></Link></section></main></Layout>;
  }
  const others=JOURNEYS.filter(o=>o.slug!==slug);
  return <Layout><main className="subpage journey">
    <section className="journey-hero" style={{backgroundImage:`url(${j.heroImage})`}}>
      <div className="journey-hero-copy">
        <p className="eyebrow" data-testid="journey-destination">{j.destination}</p>
        <h1 data-testid="journey-title">{j.title}</h1>
        <p className="journey-subtitle">{j.subtitle}</p>
        <p className="journey-duration">{j.duration}</p>
      </div>
    </section>

    <section className="journey-opening band">
      <p className="journey-opening-line" data-testid="journey-opening">{j.openingLine}</p>
      <p className="journey-opening-body">{j.supportingText}</p>
    </section>

    <section className="journey-idea band">
      <div className="section-index">01 / THE IDEA</div>
      <div className="journey-idea-body">
        {j.ideaParagraphs.map((p,i)=><p key={`${j.slug}-idea-${i}`} data-testid={`journey-idea-${i}`}>{p}</p>)}
      </div>
    </section>

    <section className="journey-itinerary band">
      <div className="section-index">02 / THE JOURNEY, DAY BY DAY</div>
      <div className="journey-days">
        {j.days.map(d=>(
          <article className="journey-day" data-testid={`journey-day-${d.num}`} key={d.num}>
            <div className="journey-day-mark">
              <span className="journey-day-num">{d.num}</span>
              <p className="eyebrow">DAY {d.num} · {d.name}</p>
            </div>
            <div className="journey-day-body">
              <p className="journey-day-lede">{d.lede}</p>
              {d.sections.map((s,si)=>(
                <div className="journey-day-section" key={`${d.num}-sec-${si}`}>
                  {s.heading && <p className="journey-day-heading">{s.heading}</p>}
                  {s.body.map((b,bi)=><p key={`${d.num}-${si}-b-${bi}`} className="journey-day-text">{b}</p>)}
                  {s.note && <p className="journey-day-note">— {s.note}</p>}
                </div>
              ))}
              {d.personalisation && (
                <div className="journey-personalisation" data-testid={`journey-personalisation-${d.num}`}>
                  <p className="eyebrow">— NOUA TOUCH · PERSONALISATION</p>
                  <div className="journey-personalisation-grid">
                    {d.personalisation.options.map((o)=>(
                      <div className="journey-personalisation-item" key={o.title}>
                        <p className="eyebrow">{o.title}</p>
                        <p>{o.text}</p>
                      </div>
                    ))}
                  </div>
                  {d.personalisation.closing && <p className="journey-personalisation-closing">{d.personalisation.closing}</p>}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>

    {j.closingLine && (
      <section className="journey-closing band">
        <p className="eyebrow">— A NOUA JOURNEY</p>
        <p className="journey-closing-line" data-testid="journey-closing">{j.closingLine}</p>
        <Link className="button dark" data-testid="journey-begin-cta" to={`/begin?path=curated`}>Begin your journey <ArrowRight size={16}/></Link>
      </section>
    )}

    {others.length>0 && (
      <section className="journey-more band">
        <p className="eyebrow">MORE JOURNEYS</p>
        <div className="journey-more-grid">
          {others.map(o=>(
            <Link to={`/journeys/${o.slug}`} className="journey-card" data-testid={`journey-related-${o.slug}`} key={o.slug}>
              <div className="journey-card-image">
                <img src={o.cardImage} alt={o.cardTitle}/>
              </div>
              <p className="eyebrow">{o.destination}</p>
              <h3>{o.cardTitle}</h3>
              <span className="text-link">Explore <ArrowRight size={16}/></span>
            </Link>
          ))}
        </div>
      </section>
    )}
  </main></Layout>;
}

function ScrollToTop(){
  const {pathname}=useLocation();
  const navType=useNavigationType();
  useEffect(()=>{
    if(navType!=='POP'){
      window.scrollTo(0,0);
    }
  },[pathname,navType]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop/>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/begin" element={<Begin />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/curated" element={<StaticPage type="curated" />} />
          <Route path="/collective" element={<StaticPage type="collective" />} />
          <Route path="/concierge" element={<StaticPage type="concierge" />} />
          <Route path="/about" element={<StaticPage type="about" />} />
          <Route path="/journal" element={<StaticPage type="journal" />} />
          <Route path="/journal/:slug" element={<Story />} />
          <Route path="/journeys" element={<JourneysList />} />
          <Route path="/journeys/:slug" element={<JourneyDetail />} />
          <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
